import React, { useEffect, useRef } from 'react';
import { Dialog } from 'primereact/dialog';
import { InfoCardData } from 'Interfaces/ProductWikiInterfaces';
import { getTagText } from '../../../utils/tagUtils';
import './ContractExpirationDetailModal.styles.scss';
import 'primereact/resources/themes/lara-light-blue/theme.css';
import 'primereact/resources/primereact.min.css';

const CONCEPT_COLORS = [
  '#3B82F6', // Blue
  '#10B981', // Emerald
  '#F59E0B', // Amber
  '#EF4444', // Red
  '#8B5CF6', // Violet
  '#EC4899', // Pink
  '#06B6D4', // Cyan
  '#F97316', // Orange
  '#6366F1', // Indigo
  '#84CC16', // Lime
];

// Wraps the matched substring in a <mark> using the concept's own color.
const highlightText = (text: string, term: string, color: string, opacity = '28'): React.ReactNode => {
  if (!term) return text;
  const idx = text.toLowerCase().indexOf(term.toLowerCase());
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <mark
        className="ce-concept-match"
        style={{
          background: `${color}${opacity}`,
          color,
          borderColor: `${color}60`,
        }}
      >
        {text.slice(idx, idx + term.length)}
      </mark>
      {text.slice(idx + term.length)}
    </>
  );
};

interface ContractExpirationDetailModalProps {
  visible: boolean;
  onHide: () => void;
  data: InfoCardData | null;
  highlightTerm?: string;
  ndcHighlightTerm?: string;
}

const ContractExpirationDetailModal: React.FC<ContractExpirationDetailModalProps> = ({
  visible,
  onHide,
  data,
  highlightTerm = '',
  ndcHighlightTerm = '',
}) => {
  const hasConceptNdcMap = Boolean(data?.concept_ndc_map && data.concept_ndc_map.length > 0);
  const hasNdcs = Boolean(data?.ndcs && data.ndcs.length > 0);
  const hasConcepts = Boolean(data?.concept_name);
  const term = highlightTerm.trim();
  const ndcTerm = ndcHighlightTerm.trim();

  const ndcScrollRef = useRef<HTMLDivElement>(null);

  // Scroll to the first matched group (concept match takes priority; NDC match
  // is used as fallback) whenever the modal opens or either term changes.
  useEffect(() => {
    const hasAnyTerm = term || ndcTerm;

    if (!visible || !hasAnyTerm) {
      if (ndcScrollRef.current) ndcScrollRef.current.scrollTop = 0;
      return;
    }

    // Wait for the Dialog open animation to settle before measuring positions.
    const timer = setTimeout(() => {
      const container = ndcScrollRef.current;
      if (!container) return;

      // Prefer concept match; fall back to NDC match.
      const matched = (
        container.querySelector('.ce-concept-group--matched') ??
        container.querySelector('.ce-concept-group--ndc-matched')
      ) as HTMLElement | null;

      if (!matched) return;

      // getBoundingClientRect gives viewport-relative coords so the calculation
      // is accurate regardless of CSS positioning on ancestor elements.
      const containerRect = container.getBoundingClientRect();
      const matchedRect = matched.getBoundingClientRect();
      const targetScrollTop =
        container.scrollTop + (matchedRect.top - containerRect.top) - 8;

      container.scrollTo({ top: Math.max(0, targetScrollTop), behavior: 'smooth' });
    }, 200);

    return () => clearTimeout(timer);
  }, [visible, term, ndcTerm]);

  return (
    <Dialog
      visible={visible}
      onHide={onHide}
      header={null}
      className="ce-detail-modal"
      modal
      dismissableMask
      draggable={false}
      resizable={false}
      style={{ width: '90vw', maxWidth: '860px' }}
    >
      {data ? (
        <div className="ce-detail-modal-content">

          {/* ── Header: title + expiration date ── */}
          <div className="ce-detail-modal-header">
            <h2 className="ce-detail-modal-title">{data.contract_number}</h2>
            <div className="ce-detail-modal-date">{data.date}</div>
          </div>

          {/* ── Details grid ── */}
          <div className="ce-detail-modal-details">
            {data.contract_owner && (
              <div className="ce-detail-row">
                <span className="ce-detail-label">Contract Owner</span>
                <span className="ce-detail-value">{data.contract_owner}</span>
              </div>
            )}
            {data.contract_number && (
              <div className="ce-detail-row">
                <span className="ce-detail-label">Contract Number</span>
                <span className="ce-detail-value">{data.contract_number}</span>
              </div>
            )}
            {data.agency && (
              <div className="ce-detail-row">
                <span className="ce-detail-label">Agency</span>
                <span className="ce-detail-value">{data.agency}</span>
              </div>
            )}
            {data.contract_type && (
              <div className="ce-detail-row">
                <span className="ce-detail-label">Contract Type</span>
                <span className="ce-detail-value">{data.contract_type}</span>
              </div>
            )}
          </div>

          {/* ── Color-coded Concept → NDC mapping ── */}
          {hasConceptNdcMap ? (
            <div className="ce-detail-ndc-section">
              <div className="ce-detail-ndc-label">NDCs</div>
              <div className="ce-detail-ndc-scroll" ref={ndcScrollRef}>
                {data.concept_ndc_map!.map((entry, index) => {
                  const color = CONCEPT_COLORS[index % CONCEPT_COLORS.length];

                  const isConceptMatch =
                    term !== '' &&
                    entry.concept_name.toLowerCase().includes(term.toLowerCase());

                  const isNdcMatch =
                    ndcTerm !== '' &&
                    entry.ndcs.some(n => n.toLowerCase().includes(ndcTerm.toLowerCase()));

                  // Sort matching NDCs to the top within this group (Option B)
                  const sortedNdcs =
                    ndcTerm
                      ? [
                          ...entry.ndcs.filter(n =>
                            n.toLowerCase().includes(ndcTerm.toLowerCase())
                          ),
                          ...entry.ndcs.filter(
                            n => !n.toLowerCase().includes(ndcTerm.toLowerCase())
                          ),
                        ]
                      : entry.ndcs;

                  const groupClass = [
                    'ce-concept-group',
                    isConceptMatch ? 'ce-concept-group--matched' : '',
                    isNdcMatch && !isConceptMatch ? 'ce-concept-group--ndc-matched' : '',
                  ]
                    .filter(Boolean)
                    .join(' ');

                  return (
                    <div
                      key={index}
                      className={groupClass}
                      style={isConceptMatch || isNdcMatch ? { borderLeftColor: color } : undefined}
                    >
                      <div className="ce-concept-group-header">
                        <span className="ce-concept-dot" style={{ background: color }} />
                        <span className="ce-concept-group-name" style={{ color }}>
                          {term
                            ? highlightText(entry.concept_name, term, color)
                            : entry.concept_name}
                        </span>
                      </div>
                      <div className="ce-concept-group-ndcs">
                        {ndcTerm
                          ? sortedNdcs.map((ndc, i) => (
                              <React.Fragment key={i}>
                                {i > 0 && ', '}
                                {highlightText(ndc, ndcTerm, color, '18')}
                              </React.Fragment>
                            ))
                          : sortedNdcs.join(', ')}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <>
              {hasConcepts && (
                <div className="ce-detail-ndc-section">
                  <div className="ce-detail-ndc-label">Concept Names</div>
                  <div className="ce-detail-ndc-scroll">{data.concept_name}</div>
                </div>
              )}
              {hasNdcs && (
                <div className="ce-detail-ndc-section">
                  <div className="ce-detail-ndc-label">NDCs</div>
                  <div className="ce-detail-ndc-scroll" ref={ndcScrollRef}>
                    {data.ndcs!.join(', ')}
                  </div>
                </div>
              )}
            </>
          )}

          {/* ── Tags ── */}
          {data.tags && data.tags.length > 0 && (
            <div className="ce-detail-modal-tags">
              {data.tags.map((tag, index) => (
                <span key={index} className={`ce-detail-modal-tag ${tag.type}`}>
                  {getTagText(tag, false)}
                </span>
              ))}
            </div>
          )}

        </div>
      ) : (
        <div className="ce-detail-modal-content">
          <div className="ce-detail-empty">No data available</div>
        </div>
      )}
    </Dialog>
  );
};

export default ContractExpirationDetailModal;
